import type { Component } from 'vue'
import cloneDeep from 'lodash/cloneDeep.js'
import type { ModalConfig, ModalState, ModalSize } from 'store'

export const useModalStore = defineStore('modal.store', {
  state: (): ModalState => ({
    modals: {},
  }),
  getters: {
    isAnyModalShown: (state) => Object.keys(state.modals).length,
    showCommonOverlay: (state) =>
      Object.values(state.modals).find(
        (modal) => modal.modalTeleportTarget === 'default',
      ),
    isCommonOverlayShown: (state) =>
      Object.values(state.modals).some(
        (modal) => modal.modalTeleportTarget === 'default',
      ),
    isAnyModalDisabledClosing: (state) =>
      Object.values(state.modals).some((modal) => modal.disableClosing),
  },
  actions: {
    // служебные функции,
    // если надо будет мутировать
    // значения внутри уже открытой модалки
    setModalComponent(key: string, component: Component) {
      this.modals[key].component = cloneDeep(component)
    },
    setModalShowCloseButton(key: string, value: boolean) {
      this.modals[key].isModalCloseButtonShown = value
    },
    setModalTeleport(key: string, target: string) {
      this.modals[key].modalTeleportTarget = target
    },
    setModalSize(key: string, size: ModalSize) {
      this.modals[key].modalSize = size
    },
    /**
     * открывает модалку
     * @param key
     * @param modalConfig
     */
    showModal({ key, modalConfig }: { key: string; modalConfig: ModalConfig }) {
      if (process.server) return
      if (!modalConfig.modalSize) modalConfig.modalSize = 'big'
      if (!modalConfig.type) modalConfig.type = 'default'
      if (!modalConfig.modalTeleportTarget)
        modalConfig.modalTeleportTarget = 'default'

      this.modals[key] = modalConfig
    },
    /**
     * закрывает модалку, сбрасывая все в дефолт
     */
    closeModal(key: string | 'closeAll') {
      const onModalClosedHandlers: (() => void)[] = []

      const getAllOnModalClosedHandlers = () => {
        Object.values(this.modals).forEach((modal) => {
          modal?.onModalClosed &&
            onModalClosedHandlers.push(modal.onModalClosed)
        })
      }
      const currentOnModalClosedHandler = () => {
        const onModalClosed = this.modals[key]?.onModalClosed

        onModalClosed && onModalClosedHandlers.push(onModalClosed)
      }

      let components = { ...this.modals }

      if (key === 'closeAll') {
        getAllOnModalClosedHandlers()
        components = {}
      } else {
        currentOnModalClosedHandler()
        delete components[key]
      }

      this.modals = components

      onModalClosedHandlers.forEach((handler) => handler())
    },

    isModalShown(key: string) {
      return key in this.modals
    },

    onAfterShow(key: string) {
      const afterShowHandler = this.modals[key]?.onAfterShow

      if (afterShowHandler) {
        afterShowHandler()
      }
    },
  },
})
